/**
 * API Service
 * Real implementation that connects to the Kielis backend API
 * TASK-297: Added getLastMeetingForClient using httpClient
 */

import { API_CONFIG } from '../../config/api-config';
import { httpClient } from '../../lib/httpClient';

const API_BASE_URL = API_CONFIG.GATEWAY_URL;

export interface SimpleClient {
  client_id: string;
  first_name: string;
  last_name: string;
  client_name: string;
  client_type: string;
  client_classification: string;
  risk_profile: string;
  total_aum: number;
  preferred_language: string;
  communication_style: string;
  investment_experience: string;
  last_meeting_date?: string;
  portfolio?: {
    total_market_value: number;
    ytd_return: number;
    unrealized_pnl_percentage: number;
    number_of_positions: number;
  };
}

export interface Portfolio {
  client_id: string;
  summary: {
    total_market_value: number;
    ytd_return: number;
    unrealized_pnl_percentage: number;
    number_of_positions: number;
    total_cash: number;
    last_updated?: string;
  };
  asset_allocation: Array<{
    asset_class: string;
    percentage: number;
    value: number;
  }>;
}

export interface Meeting {
  meeting_id: string;
  session_id: string;
  meeting_data: any;
  client_name: string;
  client_id: string;
  client_classification: string;
  client_aum: number;
  advisor_name: string;
  scheduled_at?: string;
  duration_minutes: number;
  meeting_type: string;
  meeting_subtype?: string;
  priority: string;
  preparation_status: string;
  preparation_progress: number;
  preparation_started_at?: string;
  created_at?: string;
}

/**
 * TASK-297: Response for last meeting endpoint
 */
export interface LastMeetingResponse {
  client_id: string;
  has_previous_meeting: boolean;
  last_meeting: {
    session_id: string;
    meeting_type: string;
    scheduled_at: string | null;
    ended_at: string | null;
    duration_minutes: number | null;
  } | null;
}

class APIService {
  private baseUrl: string;

  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  private async fetchWithErrorHandling<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get all clients
   */
  async getClients(): Promise<{ clients: SimpleClient[] }> {
    return this.fetchWithErrorHandling<{ clients: SimpleClient[] }>('/clients');
  }

  /**
   * Get client details by ID
   */
  async getClient(clientId: string): Promise<SimpleClient> {
    return this.fetchWithErrorHandling<SimpleClient>(`/clients/${clientId}`);
  }

  /**
   * Get client portfolio
   */
  async getClientPortfolio(clientId: string): Promise<Portfolio> {
    return this.fetchWithErrorHandling<Portfolio>(`/clients/${clientId}/portfolio`);
  }

  // TASK-648: getMeetings() removed — it called the deleted /meetings alias
  // (307 loop) and had no callers. Use listMeetings() in meetingApi.ts.

  /**
   * Get meeting details
   */
  async getMeeting(sessionId: string): Promise<Meeting> {
    return this.fetchWithErrorHandling<Meeting>(`/meeting/${sessionId}/details`);
  }

  /**
   * Start meeting preparation
   */
  async startPreparation(sessionId: string, clientData: any, portfolioData: any, interactionHistory: any = []) {
    return this.fetchWithErrorHandling(`/preparation/${sessionId}/prepare`, {
      method: 'POST',
      body: JSON.stringify({
        client_data: clientData,
        portfolio_data: portfolioData,
        interaction_history: interactionHistory
      })
    });
  }

  /**
   * Get preparation status
   */
  async getPreparationStatus(sessionId: string) {
    return this.fetchWithErrorHandling(`/meeting/${sessionId}/preparation-status`);
  }

  /**
   * Get meeting insights
   */
  async getMeetingInsights(sessionId: string) {
    return this.fetchWithErrorHandling(`/meeting/${sessionId}/insights`);
  }

  /**
   * TASK-297: Get last completed meeting for a client
   * Used for calculating search date range in meeting preparation
   * Uses httpClient for proper auth headers
   */
  async getLastMeetingForClient(clientId: string): Promise<LastMeetingResponse> {
    return httpClient.get<LastMeetingResponse>(`/meeting/clients/${clientId}/last-meeting`);
  }
}

export const apiService = new APIService();